import { Injectable } from '@angular/core';

import { BehaviorSubject, combineLatest, filter, map, Observable, pairwise, startWith, switchMap, tap } from 'rxjs';

import { Invite } from '@shared/invite/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import { InviteRole, InviteStatus } from '@shared/invite/enums';
import { LocationApiService } from '../../../../services';
import { InvitesFilters } from '../models';
import {
  AuthRestaurantService,
  InvitesQuery,
  InvitesStore,
  SocketService,
} from '@tablesready/frontend-store';

@Injectable()
export class InvitesService {
  constructor(
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly query: InvitesQuery,
    private readonly store: InvitesStore,
    private readonly socketService: SocketService,
    private readonly locationApiService: LocationApiService,
  ) {}

  load(): Observable<Invite[]> {
    return this.authRestaurantService.selectAndDetectLocationChanges()
      .pipe(
        switchMap(() => this.locationApiService.getInvites()),
        tap(invites => this.store.set(invites)),
      );
  }

  selectAll(): Observable<Invite[]> {
    return this.query.selectAll();
  }

  selectExcludedAdmins(): Observable<Invite[]> {
    return this.query.selectAll()
      .pipe(
        map(invites => invites.filter(invite => invite.role !== InviteRole.admin)),
      );
  }

  listenToChanges() {
    return this.socketService.getEventStream<void>(WebSocketEventsEnum.invite)
      .pipe(switchMap(() => this.load()));
  }

  filter(filters: BehaviorSubject<InvitesFilters>) {
    return combineLatest([
      this.selectExcludedAdmins(),
      filters.pipe(
        startWith(null),
        pairwise(),
        filter(([prev, current]) => prev === null ||
          prev.email !== current.email ||
          prev.role !== current.role ||
          prev.status !== current.status,
        ),
        map(([, current]) => current),
      ),
    ])
      .pipe(map(([invites, {email, role, status}]) => invites.filter(invite => {
        return this.compareByEmail(email, invite) &&
          this.compareByRole(role, invite) &&
          this.compareByStatus(status, invite);
      })));
  }

  private compareByEmail(searchQuery: string, invite: Invite): boolean {
    return searchQuery === '' || invite.email.includes(searchQuery);
  }

  private compareByRole(role: InviteRole | null, invite: Invite): boolean {
    return role === null || invite.role === role;
  }

  private compareByStatus(status: InviteStatus | null, invite: Invite): boolean {
    return status === null || invite.status === status;
  }
}
